<template>
  <!--
    冻结界面：导航账号信息卡片。
    结构 / 文案 / 视觉 / 交互不得擅自改动。
    权威说明：docs/导航账号信息界面冻结说明.md
  -->
  <div v-if="user" class="user-dropdown-container">
    <button
      ref="accountMenuTrigger"
      class="user-profile-trigger"
      type="button"
      aria-haspopup="menu"
      aria-controls="account-menu"
      :aria-expanded="String(isProfileMenuOpen)"
      :aria-label="`${displayName}的账号菜单`"
      @click="toggleProfileMenu"
    >
      <img
        v-if="avatarUrl && !avatarLoadFailed"
        :src="avatarUrl"
        alt=""
        class="nav-avatar"
        @error="handleAvatarError"
      />
      <span v-else class="nav-avatar-placeholder" aria-hidden="true">
        {{ accountInitial }}
      </span>
    </button>

    <Transition name="account-menu">
      <section
        id="account-menu"
        v-if="isProfileMenuOpen"
        class="profile-dropdown"
        role="menu"
        aria-label="AutoSquare 账号"
        tabindex="-1"
        @click.stop
        @keydown.esc="closeProfileMenu(true)"
      >
        <div class="dropdown-header">
          <div class="dropdown-avatar-large">
            <img
              v-if="avatarUrl && !avatarLoadFailed"
              :src="avatarUrl"
              alt=""
              class="account-avatar-image"
              @error="handleAvatarError"
            />
            <span v-else aria-hidden="true">{{ accountInitial }}</span>
          </div>
          <div class="dropdown-user-info">
            <p class="dropdown-greeting">{{ greetingText }}</p>
            <p v-if="displayEmail" class="dropdown-email">{{ displayEmail }}</p>
          </div>
          <a
            href="/api/auth/management/enter/?target=account&amp;return_path=/settings/profile"
            target="_blank"
            rel="noopener noreferrer"
            class="manage-account-btn"
            role="menuitem"
            @click="closeProfileMenu()"
          >
            管理 AutoSquare 账号
          </a>
        </div>

        <div class="dropdown-divider"></div>
        <div class="dropdown-actions">
          <a
            href="/api/auth/management/enter/?target=account&amp;return_path=/settings/emails"
            target="_blank"
            rel="noopener noreferrer"
            class="dropdown-item"
            role="menuitem"
            @click="closeProfileMenu()"
          >
            <svg class="dropdown-icon" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"></path>
            </svg>
            密码和安全
          </a>
          <a
            href="/api/auth/management/enter/?target=account&amp;return_path=/settings/profile"
            target="_blank"
            rel="noopener noreferrer"
            class="dropdown-item"
            role="menuitem"
            @click="closeProfileMenu()"
          >
            <svg class="dropdown-icon" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 20h9"></path>
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
            </svg>
            自定义个人资料
          </a>
        </div>

        <div class="dropdown-divider"></div>
        <div class="dropdown-footer">
          <button
            type="button"
            role="menuitem"
            :disabled="isLoggingOut"
            class="dropdown-item logout-dropdown-btn"
            @click.stop="logout"
          >
            <svg class="dropdown-icon" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
            {{ isLoggingOut ? '正在退出…' : '退出登录' }}
          </button>
        </div>
      </section>
    </Transition>
  </div>
  <a v-else :href="loginUrl" class="login-btn">登录</a>
</template>

<script>
import { getUserInitial } from '../utils/token'

export default {
  name: 'AccountMenu',
  props: {
    user: {
      type: Object,
      default: null
    },
    accountPortalUrl: {
      type: String,
      required: true
    },
    loginUrl: {
      type: String,
      required: true
    },
    logoutAction: {
      type: Function,
      required: true
    }
  },
  data() {
    return {
      isProfileMenuOpen: false,
      avatarLoadFailed: false,
      isLoggingOut: false
    }
  },
  computed: {
    displayName() {
      return this.user?.username?.trim() || this.user?.email?.trim() || 'AutoSquare 用户'
    },
    displayUsername() {
      const name = this.user?.username?.trim()
      if (!name) return ''
      const email = this.user?.email?.trim()
      if (email && name === email) return ''
      return name
    },
    displayEmail() {
      return this.user?.email?.trim() || this.user?.sub?.trim() || ''
    },
    infoName() {
      return this.displayUsername || this.displayEmail
    },
    greetingText() {
      if (this.displayUsername) return `你好，${this.displayUsername}！`
      return '你好！'
    },
    accountInitial() {
      return getUserInitial(this.user)
    },
    avatarUrl() {
      return this.user?.avatar_url?.trim() || ''
    }
  },
  watch: {
    avatarUrl() {
      this.avatarLoadFailed = false
    },
    user() {
      this.closeProfileMenu()
    }
  },
  mounted() {
    window.addEventListener('click', this.closeProfileMenuOnClickOutside)
    window.addEventListener('keydown', this.handleGlobalKeydown)
  },
  unmounted() {
    window.removeEventListener('click', this.closeProfileMenuOnClickOutside)
    window.removeEventListener('keydown', this.handleGlobalKeydown)
  },
  methods: {
    async logout() {
      if (this.isLoggingOut) return
      this.isLoggingOut = true
      try {
        await this.logoutAction()
        this.closeProfileMenu()
      } finally {
        this.isLoggingOut = false
      }
    },
    toggleProfileMenu(event) {
      event.stopPropagation()
      this.isProfileMenuOpen = !this.isProfileMenuOpen
    },
    closeProfileMenu(restoreFocus = false) {
      this.isProfileMenuOpen = false
      if (restoreFocus) {
        this.$nextTick(() => this.$refs.accountMenuTrigger?.focus())
      }
    },
    handleAvatarError() {
      this.avatarLoadFailed = true
    },
    handleGlobalKeydown(event) {
      if (event.key === 'Escape' && this.isProfileMenuOpen) {
        this.closeProfileMenu(true)
      }
    },
    closeProfileMenuOnClickOutside(event) {
      if (!this.isProfileMenuOpen) return
      if (!this.$el.contains(event.target)) {
        this.isProfileMenuOpen = false
      }
    }
  }
}
</script>

<style scoped>
.user-dropdown-container {
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
}

.user-profile-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: none;
  border-radius: 50%;
  background: linear-gradient(135deg, #2563eb, #3b82f6 64%, #42cfe0);
  cursor: pointer;
  font: inherit;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.22);
  transition: transform 0.2s, box-shadow 0.2s, filter 0.2s;
}

.user-profile-trigger:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.28);
  filter: brightness(1.05);
}

.nav-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  border: none;
  display: block;
  background: #fff;
}

.nav-avatar-placeholder {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--accent-color, #2563eb);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 1rem;
}

.profile-dropdown {
  /* 对齐 git a794373：账号下拉专用 Inter，与全站一致 */
  --menu-font: 'Inter', sans-serif;
  --menu-title-size: 17px;
  --menu-subtitle-size: 14px;
  --menu-body-size: 14px;
  --menu-action-size: 15px;
  position: absolute;
  top: calc(100% + 10px);
  right: -10px;
  width: 320px;
  max-width: calc(100vw - 32px);
  background: linear-gradient(145deg, #ffffff, #f8fbff);
  border: 1px solid rgba(37, 99, 235, 0.08);
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12), 0 0 1px rgba(0, 0, 0, 0.05);
  z-index: 1000;
  overflow: hidden;
  cursor: default;
  font-family: var(--menu-font);
  font-size: var(--menu-body-size);
  font-weight: 400;
  line-height: 1.4;
  color: var(--text-primary, #0f172a);
}

.profile-dropdown a::after {
  display: none !important;
}

.profile-dropdown a,
.profile-dropdown a:hover,
.profile-dropdown a.active,
.profile-dropdown a.router-link-exact-active {
  color: var(--text-primary, #0f172a);
}

.dropdown-header {
  padding: 24px 20px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background:
    radial-gradient(circle at 18% 0%, rgba(66, 207, 224, 0.1), transparent 43%),
    radial-gradient(circle at 88% 18%, rgba(37, 99, 235, 0.1), transparent 44%),
    linear-gradient(180deg, #f8fbff 0%, #f8fafc 100%);
}

.dropdown-avatar-large {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  padding: 2px;
  background: linear-gradient(135deg, #2563eb, #3b82f6 64%, #42cfe0);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 34px;
  font-weight: 500;
  margin-bottom: 12px;
  box-shadow: 0 6px 18px rgba(37, 99, 235, 0.18);
  overflow: hidden;
}

.account-avatar-image {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  background: transparent;
}

.dropdown-user-info {
  width: 100%;
  margin-bottom: 16px;
}

.dropdown-greeting {
  max-width: 100%;
  margin: 0 0 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: var(--menu-font);
  font-size: var(--menu-title-size);
  line-height: 1.4;
  font-weight: 700;
  letter-spacing: normal;
  color: var(--text-primary, #0f172a);
}

.dropdown-email {
  max-width: 100%;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: var(--menu-font);
  font-size: var(--menu-subtitle-size);
  line-height: 1.4;
  font-weight: 400;
  color: var(--text-secondary, #64748b);
}

.profile-dropdown a.manage-account-btn,
.manage-account-btn {
  margin-top: 0;
  min-height: auto;
  padding: 8px 16px;
  display: inline-block;
  border-radius: 20px;
  border: 1px solid #cbd5e1;
  color: var(--text-primary, #0f172a);
  font-family: var(--menu-font);
  font-size: var(--menu-action-size);
  font-weight: 500;
  line-height: 1.4;
  text-decoration: none;
  transition: background-color 0.2s, border-color 0.2s;
  background: #ffffff;
}

.profile-dropdown a.manage-account-btn:hover,
.manage-account-btn:hover {
  color: var(--text-primary, #0f172a);
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.dropdown-divider {
  height: 1px;
  background-color: #e2e8f0;
  margin: 0;
}

.dropdown-actions,
.dropdown-footer {
  padding: 8px 0;
}

.dropdown-item {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: auto;
  padding: 10px 20px;
  text-decoration: none;
  color: var(--text-primary, #0f172a);
  font-family: var(--menu-font);
  font-size: var(--menu-action-size);
  font-weight: 400;
  line-height: 1.4;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.2s;
}

.dropdown-item:hover {
  color: var(--text-primary, #0f172a);
  background-color: #f1f5f9;
}

.dropdown-item:disabled {
  cursor: wait;
  opacity: 0.62;
}

.dropdown-icon {
  width: 18px;
  height: 18px;
  margin-right: 12px;
  flex-shrink: 0;
  color: var(--text-secondary, #64748b);
}

.logout-dropdown-btn {
  color: var(--text-primary, #0f172a);
}

.login-btn {
  background-color: var(--accent-color, #2563eb);
  color: white !important;
  padding: 0.5rem 1.2rem;
  border-radius: 6px;
  font-weight: 600;
  text-decoration: none;
  transition: background-color 0.2s;
}

.login-btn:hover {
  background-color: var(--accent-hover, #1d4ed8);
  color: white !important;
}

.login-btn::after {
  display: none !important;
}

.account-menu-enter-active,
.account-menu-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
  transform-origin: top right;
}

.account-menu-enter-from,
.account-menu-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(0.98);
}

@media (max-width: 768px) {
  .profile-dropdown {
    position: fixed;
    top: 76px;
    right: 16px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .account-menu-enter-active,
  .account-menu-leave-active {
    transition: none;
  }
}
</style>
